"use client";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

import Check from "@/components/icons/Check";
import { HeroStickyQuizJumpControls } from "@/components/Hero/components/HeroStickyQuizJumpControls";
import type { HeroProps } from "../types";

/**
 * example input: "font-bold text-3xl lg:text-4xl"
 * output: "[&_h1]:font-bold [&_h1]:text-3xl lg:[&_h1]:text-4xl"
 */
const getTitleClassNames = (titleClassName: string | undefined) =>
  titleClassName
    ?.split(" ")
    .map((className) => {
      if (className.includes(":")) {
        return className
          .split(":")
          .map((item, index) => (index === 1 ? `[&_h1]:${item}` : item))
          .join(":");
      }
      return `[&_h1]:${className}`;
    })
    .join(" ") || "";

const isViewportMobile = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 767px)").matches;

const BackgroundImage = ({
  children,
  image,
  mobileImage,
  titleClassName,
  leadingAiIcon = false,
  heroSupportBullets,
  stickyMobileQuizJump = false,
  stickyQuizJumpLabel,
}: HeroProps) => {
  const titleClassNames = getTitleClassNames(titleClassName);

  const pathname = usePathname();
  const isProfEdPage = pathname === "/professional-education";
  const shouldHideImageOnMobile = !isProfEdPage && !mobileImage?.sourceUrl;

  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0 });
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    setIsMobileViewport(isViewportMobile());
    const mq = window.matchMedia("(max-width: 767px)");
    const handleChange = () => setIsMobileViewport(mq.matches);
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  const showStickyBar = useMemo(
    () => Boolean(stickyMobileQuizJump && isMobileViewport && !heroInView),
    [heroInView, isMobileViewport, stickyMobileQuizJump],
  );

  const bullets = (heroSupportBullets ?? []).filter(
    (line) => line.trim().length > 0,
  );

  return (
    <div
      ref={heroRef}
      className={
        image
          ? `relative md:px-4 pt-16 pb-8 lg:p-20 mx-break-out lg:bg-none ${shouldHideImageOnMobile ? "bg-primary-gradient from-secondary to-primary-500" : ""} [&_p]:text-white [&_p]:text-xl lg:[&_p]:text-[28px] [&_p]:font-bold ${titleClassNames || "[&_h1]:text-white [&_h1]:elm-d3 [&_h1]:2xl:elm-d4 [&_h1]:!leading-none [&_h1]:italic [&_h1]:font-black [&_h1]:mb-8"}`
          : titleClassNames
      }
    >
      {mobileImage?.sourceUrl && (
        <Image
          src={mobileImage.sourceUrl}
          priority
          fill
          alt={mobileImage.altText ?? ""}
          className="bg-putty-700 absolute object-cover object-right m-0 z-[-1] block md:hidden"
        />
      )}
      {image?.sourceUrl && (
        <Image
          src={image.sourceUrl}
          priority
          fill
          alt={image.altText ?? ""}
          className={`bg-putty-700 absolute object-cover object-[right_25%] m-0 z-[-1] ${isProfEdPage ? "block" : shouldHideImageOnMobile ? "hidden lg:block" : "hidden md:block"}`}
        />
      )}
      <div className="container flex pb-8">
        <div className="lg:basis-2/3">
          {leadingAiIcon ? (
            <div className="flex items-start gap-3">
              <Image
                src="/icons/ai-icon.png"
                alt=""
                width={36}
                height={36}
                className="m-0 shrink-0 mt-1"
              />
              <div className="min-w-0 flex-1">{children}</div>
            </div>
          ) : (
            children
          )}

          {bullets.length > 0 && (
            <ul className="flex flex-wrap gap-3 sm:gap-6 list-none !p-0 !pl-0 m-0 mt-6 text-white">
              {bullets.map((item, index) => (
                <li
                  key={`support-bullet-${index}`}
                  className="flex items-center gap-1 m-0"
                >
                  <Check svgProps={{ width: "20", height: "20" }} />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {stickyMobileQuizJump && (
        <HeroStickyQuizJumpControls
          visible={showStickyBar}
          label={stickyQuizJumpLabel}
        />
      )}
    </div>
  );
};

export default BackgroundImage;
